import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { ArrowLeft, Download, Copy, Check, Loader2, Layers, Play, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { CardPreview, DeckStats, DrawSimulator, Tile, TileGrid } from '../components/sidebar';
import BlogPortableText from '../components/PortableText';
import { fetchSampleDeckBySlug } from '../lib/sanity';
import { LanguageBadge } from '../components/LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import {
  CHART_OPTIONS,
  TILE_CONFIG,
} from '../constants';
import {
  getProxyImageUrl,
  validateDeck,
  getLevelChartData,
  getCharacterChartData,
  sortCardsForDisplay,
  normalizeCard,
  shuffleArray,
  expandDeckByCount,
} from '../utils';
import '../components/DeckVisualizer.css';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

/**
 * Extract a YouTube video ID from any common URL shape.
 */
function getYouTubeId(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (u.pathname === '/watch') return u.searchParams.get('v');
      if (u.pathname.startsWith('/shorts/')) return u.pathname.split('/')[2] || null;
      if (u.pathname.startsWith('/embed/')) return u.pathname.split('/')[2] || null;
    }
    if (host === 'youtu.be') return u.pathname.replace(/^\//, '') || null;
  } catch (_) {
    return null;
  }
  return null;
}

function getVideoThumbnail(video) {
  if (video?.thumbnailUrl) return video.thumbnailUrl;
  const id = getYouTubeId(video?.url);
  return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null;
}

function getLocalizedVideoTitle(video, language) {
  if (!video || !video.title) return '';
  if (typeof video.title === 'string') return video.title;
  if (language === 'JP' && video.title.ja) return video.title.ja;
  if (language === 'KO' && video.title.ko) return video.title.ko;
  if (language === 'ZH-HANT' && video.title.zhHant) return video.title.zhHant;
  return video.title.en || '';
}

function getLocalizedVideoDescription(video, language) {
  if (!video || !video.description) return '';
  if (typeof video.description === 'string') return video.description;
  if (language === 'JP' && video.description.ja) return video.description.ja;
  if (language === 'KO' && video.description.ko) return video.description.ko;
  if (language === 'ZH-HANT' && video.description.zhHant) return video.description.zhHant;
  return video.description.en || '';
}

const KIND_LABEL = {
  showcase: 'Showcase',
  replay: 'Match replay',
  tutorial: 'Tutorial',
  tournament: 'Tournament',
};

/**
 * Renders the tabbed "Strategy / Videos / Details" panel under the card grid.
 * Tabs that have no content are hidden; Details is always available.
 */
function DeckInfoTabs({ deckInfo, language, t, description }) {
  if (!deckInfo) return null;

  const hasStrategy = Array.isArray(deckInfo.strategy) && deckInfo.strategy.length > 0;
  const hasVideos = Array.isArray(deckInfo.videos) && deckInfo.videos.length > 0;
  const defaultTab = 'details';

  const publishedDate = deckInfo.publishedAt
    ? new Date(deckInfo.publishedAt).toLocaleDateString()
    : null;

  return (
    <div className="mt-4 bg-white rounded-lg border border-border shadow-sm p-4">
      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList>
          <TabsTrigger value="details">
            {t('deckView.tabs.details') || 'Details'}
          </TabsTrigger>
          {hasVideos && (
            <TabsTrigger value="videos">
              {t('deckView.tabs.videos') || 'Videos'}
            </TabsTrigger>
          )}
          {hasStrategy && (
            <TabsTrigger value="strategy">
              {t('deckView.tabs.strategy') || 'Strategy'}
            </TabsTrigger>
          )}
        </TabsList>

        {hasStrategy && (
          <TabsContent value="strategy">
            <div className="prose prose-sm max-w-none">
              <BlogPortableText value={deckInfo.strategy} />
            </div>
          </TabsContent>
        )}

        {hasVideos && (
          <TabsContent value="videos">
            <div className="grid gap-4 sm:grid-cols-2">
              {deckInfo.videos.map((video, idx) => {
                const title = getLocalizedVideoTitle(video, language) || video.url;
                const desc = getLocalizedVideoDescription(video, language);
                const thumbnail = getVideoThumbnail(video);
                const kindLabel = video.kind ? (KIND_LABEL[video.kind] || video.kind) : null;
                const dateText = video.publishedAt
                  ? new Date(video.publishedAt).toLocaleDateString(undefined, {
                      year: 'numeric', month: 'short', day: 'numeric',
                    })
                  : null;
                return (
                  <a
                    key={video._key || idx}
                    href={video.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col rounded-lg overflow-hidden border border-border bg-card hover:shadow-md hover:border-primary/40 transition-all"
                  >
                    <div className="relative aspect-video bg-muted overflow-hidden">
                      {thumbnail ? (
                        <img
                          src={thumbnail}
                          alt={title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-4xl">🎬</div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-black/60 rounded-full w-12 h-12 flex items-center justify-center group-hover:bg-red-600 transition-colors">
                          <Play className="h-6 w-6 text-white fill-white ml-0.5" />
                        </div>
                      </div>
                      {kindLabel && (
                        <Badge variant="secondary" className="absolute top-2 left-2 shadow-sm">
                          {kindLabel}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5 p-3">
                      <h3 className="font-semibold text-base leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                        {title}
                      </h3>
                      {desc && (
                        <p className="text-sm text-muted-foreground line-clamp-3">{desc}</p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                        {dateText && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {dateText}
                          </span>
                        )}
                        {video.opponent && (
                          <span>vs {video.opponent}</span>
                        )}
                      </div>
                      <span className="text-sm text-primary font-medium mt-1">
                        {t('deckView.videos.watch') || 'Watch'} ›
                      </span>
                    </div>
                  </a>
                );
              })}
            </div>
          </TabsContent>
        )}

        <TabsContent value="details">
          <div className="grid gap-4 sm:grid-cols-2">
            <dl className="space-y-2 text-sm">
              {deckInfo.archetype && (
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">{t('deckView.details.archetype') || 'Archetype'}</dt>
                  <dd className="font-medium capitalize">{deckInfo.archetype}</dd>
                </div>
              )}
              {deckInfo.difficulty && (
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">{t('deckView.details.difficulty') || 'Difficulty'}</dt>
                  <dd className="font-medium capitalize">{deckInfo.difficulty}</dd>
                </div>
              )}
              {deckInfo.cardSet && (
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">{t('deckView.details.cardSet') || 'Card set'}</dt>
                  <dd className="font-medium">{deckInfo.cardSet}</dd>
                </div>
              )}
              {deckInfo.author && (
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">{t('deckView.details.author') || 'Author'}</dt>
                  <dd className="font-medium">{deckInfo.author}</dd>
                </div>
              )}
              {publishedDate && (
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">{t('deckView.details.published') || 'Published'}</dt>
                  <dd className="font-medium">{publishedDate}</dd>
                </div>
              )}
              {deckInfo.creditUrl && (
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">{t('deckView.details.source') || 'Source'}</dt>
                  <dd className="font-medium truncate">
                    <a href={deckInfo.creditUrl} target="_blank" rel="noopener noreferrer" className="text-primary underline">
                      {t('deckView.details.viewSource') || 'View source'}
                    </a>
                  </dd>
                </div>
              )}
            </dl>

            <div className="space-y-3 text-sm">
              {description && (
                <div>
                  <div className="text-muted-foreground mb-1">{t('deckView.details.description') || 'Description'}</div>
                  <p className="leading-relaxed">{description}</p>
                </div>
              )}
              {Array.isArray(deckInfo.characters) && deckInfo.characters.length > 0 && (
                <div>
                  <div className="text-muted-foreground mb-1">{t('deckView.details.characters') || 'Characters'}</div>
                  <div className="flex flex-wrap gap-1">
                    {deckInfo.characters.map((c) => (
                      <Badge key={c} variant="outline">{c}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {Array.isArray(deckInfo.tags) && deckInfo.tags.length > 0 && (
                <div>
                  <div className="text-muted-foreground mb-1">{t('deckView.details.tags') || 'Tags'}</div>
                  <div className="flex flex-wrap gap-1">
                    {deckInfo.tags.map((tag) => (
                      <Badge key={tag} variant="secondary">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * DeckView page - Read-only view of a deck
 * Shows deck cards, stats, and draw simulator without editing capabilities
 */
export default function DeckView() {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  // Deck state
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deckInfo, setDeckInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  // Tile collapse states
  const [collapseStats, setCollapseStats] = useState(false);
  const [collapseDrawSimulator, setCollapseDrawSimulator] = useState(false);

  // Hover preview state
  const [previewCard, setPreviewCard] = useState(null);

  // Layout mode state for TileGrid
  const [layoutMode, setLayoutMode] = useState('default');

  // Draw simulator state
  const [drawnCards, setDrawnCards] = useState([]);
  const [remainingDeck, setRemainingDeck] = useState([]);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [mulliganCount, setMulliganCount] = useState(0);

  // Load deck function
  const loadDeck = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const deck = await fetchSampleDeckBySlug(deckId);

      if (!deck) {
        setError(t('deckView.deckNotFound') || 'Deck not found');
        setLoading(false);
        return;
      }

      setDeckInfo(deck);

      if (!deck.deckCode) {
        setError(t('deckView.invalidDeckCode') || 'Deck code not available');
        setLoading(false);
        return;
      }

      // Decode deck
      const deckString = decodeURIComponent(escape(atob(deck.deckCode.trim())));
      const deckData = JSON.parse(deckString);

      if (deckData.version === 2 && deckData.cards) {
        // Fetch card details
        const cardPromises = deckData.cards.map(entry =>
          fetch(`/api/cards/${entry.id}?lang=${language}`)
            .then(res => res.json())
            .then(card => {
              const normalized = normalizeCard(card);
              return {
                ...normalized,
                count: entry.count || 1,
                _fallbackLanguage: card.languageMismatch ? card.language : null,
                _availableInLanguage: card.availableInLanguage !== false,
              };
            })
            .catch(() => null)
        );

        const fetchedCards = await Promise.all(cardPromises);
        setCards(fetchedCards.filter(Boolean));
      }
    } catch (err) {
      console.error('Failed to load deck:', err);
      setError(t('sampleDecks.deckLoadError') || 'Failed to load deck');
    } finally {
      setLoading(false);
    }
  }, [deckId, language, t]);

  // Load deck on mount or when dependencies change
  useEffect(() => {
    loadDeck();
  }, [loadDeck]);

  // Deck validation
  const deckValidation = useMemo(() => validateDeck(cards), [cards]);

  // Chart data
  const levelChartData = useCallback(() => getLevelChartData(deckValidation.stats), [deckValidation.stats]);
  const characterChartData = useCallback(() => getCharacterChartData(deckValidation.stats), [deckValidation.stats]);

  // Draw simulator functions
  const drawInitialHand = () => {
    const fullDeck = shuffleArray(expandDeckByCount(cards));
    const hand = fullDeck.slice(0, 6);
    const remaining = fullDeck.slice(6);
    setDrawnCards(hand);
    setRemainingDeck(remaining);
    setHasDrawn(true);
    setMulliganCount(0);
  };

  const mulligan = () => {
    if (mulliganCount >= 1) {
      alert(t('errors.mulliganLimit') || 'You can only mulligan once!');
      return;
    }
    if (remainingDeck.length < 6) {
      alert(t('errors.notEnoughCards') || 'Not enough cards left in deck to mulligan!');
      return;
    }
    const hand = remainingDeck.slice(0, 6);
    const remaining = remainingDeck.slice(6);
    setDrawnCards(hand);
    setRemainingDeck(remaining);
    setMulliganCount(mulliganCount + 1);
  };

  const resetDraw = () => {
    setMulliganCount(0);
    drawInitialHand();
  };

  // Copy deck code
  const copyDeckCode = async () => {
    if (deckInfo?.deckCode) {
      await navigator.clipboard.writeText(deckInfo.deckCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Get deck name
  const getDeckName = () => {
    if (!deckInfo) return 'Loading...';
    if (language === 'JP' && deckInfo.name_ja) return deckInfo.name_ja;
    return deckInfo.name;
  };

  // Get deck description
  const getDeckDescription = () => {
    if (!deckInfo) return '';
    if (language === 'JP' && deckInfo.description_ja) return deckInfo.description_ja;
    return deckInfo.description || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-48px-40px)]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-48px-40px)] gap-4">
        <p className="text-destructive text-lg">{error}</p>
        <Button onClick={() => navigate('/decks')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('deckView.backToDecks') || 'Back to Sample Decks'}
        </Button>
      </div>
    );
  }

  return (
    <div className="deck-builder-container h-full flex flex-col overflow-hidden">
      {/* Gradient Header */}
      <div className="bg-gradient-to-r from-brand to-brand-accent text-white flex-shrink-0">
        <div className="max-w-full px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/decks')}
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <Layers className="h-6 w-6" />
                  <h1 className="text-xl font-bold">{getDeckName()}</h1>
                </div>
                <p className="text-sm text-white/70 mt-0.5">
                  {getDeckDescription() || `${deckValidation.stats.totalCards} ${t('common.cards') || 'cards'}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={copyDeckCode}
                className="border border-white/40 text-white hover:bg-white/20"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-1" />
                    {t('common.copied') || 'Copied!'}
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-1" />
                    {t('deckView.copyDeckCode') || 'Copy Code'}
                  </>
                )}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/deck-builder?load=${encodeURIComponent(deckInfo?.deckCode || '')}`)}
              >
                <Download className="h-4 w-4 mr-1" />
                {t('deckView.loadInBuilder') || 'Load in Builder'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content with TileGrid */}
      <div className="flex-1 overflow-hidden p-3">
        <TileGrid
          layoutMode={layoutMode}
          onLayoutModeChange={setLayoutMode}
          rightPaneContent={
            <CardPreview card={previewCard} className="flex-shrink-0" />
          }
          searchPaneContent={
            <div className="h-full overflow-auto">
              <DeckInfoTabs
                deckInfo={deckInfo}
                language={language}
                t={t}
                description={getDeckDescription()}
              />
            </div>
          }
          deckAreaContent={
            <div className="deck-area flex flex-col min-h-0 h-full bg-white rounded-lg shadow-sm border border-border overflow-hidden">
              {/* Card Grid */}
              <div className="flex-1 overflow-auto p-3">
                <div className="card-grid" id="cardGrid">
                  {sortCardsForDisplay(cards).map((card) => {
                    const cardId = card.cardNumber || card.number || card.id;
                    const imageUrl = getProxyImageUrl(card.images?.blob?.full || card.images?.original?.full);
                    const rarityText = typeof card.rarity === 'string' ? card.rarity : card.rarity?.value || '';
                    const typeText = typeof card.type === 'string' ? card.type : card.type?.value || '';
                    const featureValue = card.feature || '';
                    const isScene = featureValue === 'scene';
                    const cardLanguage = card.language || card._fallbackLanguage;

                    return (
                      <div
                        key={cardId}
                        className={`card-wrapper${isScene ? ' scene-card' : ''}${card._fallbackLanguage ? ' language-fallback' : ''}`}
                        onMouseEnter={() => setPreviewCard(card)}
                        onFocus={() => setPreviewCard(card)}
                        tabIndex={0}
                      >
                        {imageUrl && <img src={imageUrl} alt={card.name} className="card-image" />}
                        <LanguageBadge cardLanguage={cardLanguage} selectedLanguage={language} />
                        <div className="card-info">
                          <div className="card-name">{card.name}</div>
                          <div className="card-meta">{card.number}</div>
                          {rarityText && <div className="card-rarity">{rarityText}</div>}
                          {typeText && <div className="card-type">{typeText}</div>}
                        </div>
                        <div className="card-count">x{card.count}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          }
        >
          {/* Tile: Deck Stats */}
          <Tile
            id={TILE_CONFIG.deckStats.id}
            icon={TILE_CONFIG.deckStats.icon}
            title={t(TILE_CONFIG.deckStats.titleKey) || TILE_CONFIG.deckStats.defaultTitle}
            isCollapsed={collapseStats}
            onToggle={() => setCollapseStats(!collapseStats)}
            heightUnits={TILE_CONFIG.deckStats.defaultHeight}
            minHeight={TILE_CONFIG.deckStats.minHeight}
            maxHeight={TILE_CONFIG.deckStats.maxHeight}
          >
            <DeckStats
              isCollapsed={false}
              onToggle={() => {}}
              deckValidation={deckValidation}
              getLevelChartData={levelChartData}
              getCharacterChartData={characterChartData}
              chartOptions={CHART_OPTIONS}
              cards={cards}
              t={t}
              hideHeader={true}
            />
          </Tile>

          {/* Tile: Draw Simulator */}
          <Tile
            id={TILE_CONFIG.drawSimulator.id}
            icon={TILE_CONFIG.drawSimulator.icon}
            title={t(TILE_CONFIG.drawSimulator.titleKey) || TILE_CONFIG.drawSimulator.defaultTitle}
            isCollapsed={collapseDrawSimulator}
            onToggle={() => setCollapseDrawSimulator(!collapseDrawSimulator)}
            heightUnits={TILE_CONFIG.drawSimulator.defaultHeight}
            minHeight={TILE_CONFIG.drawSimulator.minHeight}
            maxHeight={TILE_CONFIG.drawSimulator.maxHeight}
          >
            <DrawSimulator
              isCollapsed={false}
              onToggle={() => {}}
              drawnCards={drawnCards}
              remainingDeck={remainingDeck}
              hasDrawn={hasDrawn}
              mulliganCount={mulliganCount}
              deckHasCards={cards.length > 0}
              onDrawInitialHand={drawInitialHand}
              onMulligan={mulligan}
              onResetDraw={resetDraw}
              getProxyImageUrl={getProxyImageUrl}
              t={t}
              hideHeader={true}
            />
          </Tile>
        </TileGrid>
      </div>
    </div>
  );
}
